// The SLA hold panel on a call-out's own page (Session AUT). The response clock is paused while ELE is waiting on the
// CUSTOMER (awaiting payment, a signature, an approval, or a reason the office gives): no at-risk email, no missed flag,
// and the held time is left out of the response time and reported separately. The office places, releases, corrects
// and removes holds here, one click each, every one audited with who, when and why. An emergency call-out that asks
// the caller to pay before attendance is held automatically and released when the payment is recorded.
//
// Never hidden: a held call-out reads "SLA held: <reason>" here and on the Call-outs list, and a released hold stays
// listed with its time. A call-out assigned before 29 September 2026 has no recorded response time and the server
// refuses a hold on it; the refusal is shown as it comes back.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { SlaHoldItem, SlaHoldReason, SlaHoldView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { formatDateTime } from "../../lib/format";
import { formatSlaMinutes } from "../../lib/slaFormat";
import { SLA_HOLD_REASON_OPTIONS, fromLocalInputValue, toLocalInputValue } from "../../lib/slaHold";
import { Button, buttonClasses } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ModalShell } from "../../components/ModalShell";
import { StatusPill } from "../../components/StatusPill";

type FormState = { holdId: string | null; reason: SlaHoldReason; reasonOther: string; heldFrom: string; releasedAt: string };

function sourceWords(h: SlaHoldItem): string {
  if (h.source === "automatic") return "Placed automatically (payment requested before attendance)";
  return h.placedByName ? `Placed by ${h.placedByName}` : "Placed by the office";
}

function releaseWords(h: SlaHoldItem): string {
  if (!h.releasedAt) return "Still on hold";
  const when = formatDateTime(h.releasedAt);
  if (h.releasedVia === "payment") return `Released ${when} when the payment was recorded`;
  if (h.releasedVia === "assigned") return `Released ${when} when an engineer was assigned`;
  return h.releasedByName ? `Released ${when} by ${h.releasedByName}` : `Released ${when}`;
}

export function SlaHoldPanel({ calloutId, hasClock, onChanged }: { calloutId: string; hasClock: boolean; onChanged: () => void }) {
  const [view, setView] = useState<SlaHoldView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.callouts.slaHolds(calloutId));
      setError(null);
    } catch (err) {
      setError(friendlyApiError(err, "Could not load the SLA holds."));
    }
  }, [calloutId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function apply(run: () => Promise<SlaHoldView>, fallback: string): Promise<boolean> {
    setBusy(true);
    try {
      setView(await run());
      setError(null);
      onChanged();
      return true;
    } catch (err) {
      const message = friendlyApiError(err, fallback);
      if (form) setFormError(message);
      else setError(message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  function openPlace() {
    setFormError(null);
    setForm({ holdId: null, reason: "awaiting_payment", reasonOther: "", heldFrom: toLocalInputValue(new Date()), releasedAt: "" });
  }

  function openCorrect(h: SlaHoldItem) {
    setFormError(null);
    setForm({ holdId: h.id, reason: h.reason, reasonOther: h.reasonOther ?? "", heldFrom: toLocalInputValue(h.heldFrom), releasedAt: h.releasedAt ? toLocalInputValue(h.releasedAt) : "" });
  }

  async function submitForm() {
    if (!form) return;
    const heldFrom = fromLocalInputValue(form.heldFrom);
    if (!heldFrom) {
      setFormError("Give the date and time the call-out started waiting on the customer.");
      return;
    }
    if (form.reason === "other" && !form.reasonOther.trim()) {
      setFormError("Say why the call-out is on hold when the reason is Other.");
      return;
    }
    const releasedAt = fromLocalInputValue(form.releasedAt);
    const body = { reason: form.reason, reasonOther: form.reason === "other" ? form.reasonOther.trim() : null, heldFrom, releasedAt };
    const ok = form.holdId
      ? await apply(() => api.callouts.correctSlaHold(calloutId, form.holdId!, body), "Could not correct the hold.")
      : await apply(() => api.callouts.placeSlaHold(calloutId, body), "Could not place the hold.");
    if (ok) setForm(null);
  }

  if (!hasClock && (view?.holds.length ?? 0) === 0) return null;
  const open = view?.holds.find((h) => h.releasedAt == null) ?? null;

  return (
    <div className="mt-3 border-t border-ele-hairline pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-base tracking-wide text-ele-strong">SLA hold</h3>
          {view?.heldLabel && <StatusPill word={view.heldLabel} tone={view.onHold ? "amber" : "grey"} />}
        </div>
        <div className="flex flex-wrap gap-2">
          {open ? (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void apply(() => api.callouts.releaseSlaHold(calloutId), "Could not release the hold.")}>
              Release hold
            </Button>
          ) : (
            hasClock && (
              <Button variant="ghost" size="sm" disabled={busy} onClick={openPlace}>
                Place hold
              </Button>
            )
          )}
        </div>
      </div>
      <p className="mt-1 text-xs text-ele-body opacity-70">
        Pauses the response clock while we wait on the customer. Held time is left out of the response time and reported separately.
      </p>
      {view && (
        <dl className="mt-2 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-ele-body opacity-70">Time on hold</dt>
            <dd className="text-ele-strong">{view.heldMins != null && view.heldMins > 0 ? formatSlaMinutes(view.heldMins) : "None"}</dd>
          </div>
          <div>
            <dt className="text-ele-body opacity-70">Response due</dt>
            <dd className="text-ele-strong">{view.onHold ? "Paused while on hold" : view.effectiveDueAt ? formatDateTime(view.effectiveDueAt) : "Not recorded"}</dd>
          </div>
        </dl>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {view && view.holds.length > 0 && (
        <ul className="mt-2 space-y-2">
          {view.holds.map((h) => (
            <li key={h.id} className="rounded-ele border border-ele-hairline p-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ele-strong">SLA held: {h.label}</span>
                <span className="flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => openCorrect(h)}>
                    Correct
                  </Button>
                  <ConfirmButton
                    onConfirm={() => void apply(() => api.callouts.removeSlaHold(calloutId, h.id), "Could not remove the hold.")}
                    disabled={busy}
                    busy={busy}
                    label="Remove"
                    busyLabel="Removing..."
                    confirmLabel="Confirm remove?"
                    className={buttonClasses("destructive", "sm")}
                  />
                </span>
              </div>
              <p className="mt-1 text-xs text-ele-body">
                From {formatDateTime(h.heldFrom)}. {releaseWords(h)}. {sourceWords(h)}.
              </p>
            </li>
          ))}
        </ul>
      )}

      {form && (
        <ModalShell ariaLabel={form.holdId ? "Correct the SLA hold" : "Place an SLA hold"} onClose={() => !busy && setForm(null)}>
          <h2 className="font-heading text-lg tracking-wide text-ele-strong">{form.holdId ? "Correct the SLA hold" : "Place an SLA hold"}</h2>
          <p className="mt-1 text-sm text-ele-body">
            The response clock is paused for this time. To clear a missed response that was really a wait on the customer, give the time it started and the time it ended.
          </p>
          <div className="mt-3 space-y-3 text-sm">
            <label className="block">
              <span className="text-ele-body">Reason</span>
              <select
                className="mt-1 w-full rounded-ele border border-ele-hairline bg-ele-surface p-2 text-ele-strong"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value as SlaHoldReason })}
              >
                {SLA_HOLD_REASON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label.charAt(0).toUpperCase() + o.label.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            {form.reason === "other" && (
              <label className="block">
                <span className="text-ele-body">Why it is on hold</span>
                <input
                  className="mt-1 w-full rounded-ele border border-ele-hairline bg-ele-surface p-2 text-ele-strong"
                  maxLength={300}
                  value={form.reasonOther}
                  onChange={(e) => setForm({ ...form, reasonOther: e.target.value })}
                />
              </label>
            )}
            <label className="block">
              <span className="text-ele-body">On hold from</span>
              <input
                type="datetime-local"
                className="mt-1 w-full rounded-ele border border-ele-hairline bg-ele-surface p-2 text-ele-strong"
                value={form.heldFrom}
                onChange={(e) => setForm({ ...form, heldFrom: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="text-ele-body">Ended (leave empty while the call-out is still waiting)</span>
              <input
                type="datetime-local"
                className="mt-1 w-full rounded-ele border border-ele-hairline bg-ele-surface p-2 text-ele-strong"
                value={form.releasedAt}
                onChange={(e) => setForm({ ...form, releasedAt: e.target.value })}
              />
            </label>
          </div>
          {formError && (
            <p role="alert" className="mt-3 text-sm text-ele-danger">
              {formError}
            </p>
          )}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" disabled={busy} onClick={() => void submitForm()}>
              {busy ? "Saving..." : form.holdId ? "Save correction" : "Place hold"}
            </Button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
